import React, {Fragment, useMemo} from 'react';
import {Submit, Sticky, Button, Form, Edit, Textarea, Icons, Flex, List, Popup} from 'components';
import {over, shorten, noop, generate, includes, some, useNamedSelector, fromPairs, reject, openPopup} from 'helpers';
import {checkInUpdate, toggleQuestion, updateQuestion, deleteQuestion} from 'store/diary';

export function CheckIn({ type, children }) {
  const date = useNamedSelector(type == 'reflections' ? 'calendar.todayAdjustedToNight' : 'calendar.today');
  const { questions, matchedDiaryId } = useNamedSelector('diary.checkIn', { type, date });
  const blacklist = useNamedSelector('diary.questionsBlacklist');
  const hasHidden = some(questions, q => includes(blacklist, q.id));

  const initialValues = useMemo(() => ({
    answers: fromPairs(questions.map(({ id, value }) => [id, value || ''])),
    date,
    id: matchedDiaryId || generate(),
    questions: fromPairs(questions.map(({ id, key }) => [id, key || ''])),
    type,
  }), [ type ]);

  return (
    <>
      <Sticky>
        <Flex className={['middle', 'justified']}>
          { children }
          <Button className="tiny" icon="settings" onClick={openPopup(Visibility, {type})}>
            Questions
          </Button>
        </Flex>
      </Sticky>
      <Form initialValues={initialValues} onChange={checkInUpdate}>
        <List tiny-gutter>
          { reject(questions, question => includes(blacklist, question.id)).map(question =>
            <Question question={question} key={question.id} />
          ) }
        </List>
      </Form>
    </>
  );
}

function Question({ question }) {
  return (
    <Fragment>
      <Flex middle justified className="smedium-spacing">
        <b>{ question.key || 'Untitled' }</b>
        { !question.predefined &&
          <Icons.Handle className="secondary small" onClick={openPopup(QuestionContextMenu, { question, id: question.id })} />
        }
      </Flex>
      <Textarea name={'answers.' + question.id} minRows={question.key == 'What are possible solutions?' ? 3 : 1} placeholder="Enter answer.." />
    </Fragment>
  );
}

function QuestionContextMenu({ question, popup }) {
  return (
    <Popup.Menu>
      <Popup.Menu.Item icon="invisible" onClick={() => toggleQuestion(question)}>Hide</Popup.Menu.Item>
      <Popup.Menu.Item icon="pencil" onClick={popup.redirect(EditQuestion)}>Edit</Popup.Menu.Item>
      <Popup.Menu.Separator />
      <Popup.Menu.DeleteItem onConfirm={() => deleteQuestion(question)}>Delete</Popup.Menu.DeleteItem>
    </Popup.Menu>
  );
}

function EditQuestion({ question, popup }) {
  return (
    <Form initialValues={{ ...question, popup }} onSubmit={updateQuestion}>
      <Edit name="key" autoFocus placeholder="Untitled" />
    </Form>
  );
}

export function NewQuestion({questionType, toggle}) {
  return (
    <Form initialValues={{ questionType: questionType + '_manual' }}  onSubmit={over(updateQuestion, toggle)} delay className="compact">
      <Edit required name="key" autoFocus placeholder="Enter question.." />
      <Submit icon="check" />
    </Form>
  );
}

export function Visibility({type}) {
  const date = useNamedSelector(type === 'reflections' ? 'calendar.todayAdjustedToNight' : 'calendar.today');
  const {questions} = useNamedSelector('diary.checkIn', {type, date});
  const blacklist = useNamedSelector('diary.questionsBlacklist');
  return (
    <>
      <Popup.Menu onClick={noop} className="is-small">
        { questions.map(({ id, key }) =>
          <Popup.Menu.Checkbox
            children={shorten(key, { words: 6 })}
            key={id}
            checked={!includes(blacklist, id)}
            onClick={() => toggleQuestion({ id })}
          />
        ) }
      </Popup.Menu>
      <Popup.Menu onClick={noop}>
        <Popup.Menu.Separator />
        <Popup.Menu.AdvanceItem icon="plus" Component={NewQuestion} questionType={type}>New Question</Popup.Menu.AdvanceItem>
      </Popup.Menu>
    </>
  );
}
